import { useState, useEffect } from 'react';

/**
 * Detect whether the mobile virtual keyboard is open using the Visual Viewport API.
 *
 * Compares `visualViewport.height` to `window.innerHeight`. When the ratio
 * drops below a threshold (0.75), the keyboard is considered open.
 *
 * Returns `false` on browsers that don't support `window.visualViewport`.
 */
export function useKeyboardVisible(): boolean {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    // Capture the initial full height at mount time. window.innerHeight
    // can itself shrink on some Android browsers when the keyboard opens,
    // so we snapshot the "no keyboard" height once.
    const fullHeight = window.innerHeight;

    const handleResize = () => {
      const ratio = vv.height / fullHeight;
      setIsKeyboardVisible(ratio < 0.75);
    };

    vv.addEventListener('resize', handleResize);
    // Also check on scroll (some browsers fire scroll instead of resize)
    vv.addEventListener('scroll', handleResize);

    return () => {
      vv.removeEventListener('resize', handleResize);
      vv.removeEventListener('scroll', handleResize);
    };
  }, []);

  return isKeyboardVisible;
}
